import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import DataTable from '../components/Tables/DataTable';
import ProjectForm from '../components/Forms/ProjectForm';
import { Plus, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';

const Projects = () => {
    const { currency } = useAuth();
    const [projects, setProjects] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [developers, setDevelopers] = useState([]);

    // Filters
    const [searchName, setSearchName] = useState('');
    const [filterCustomer, setFilterCustomer] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');

    const fetchData = async () => {
        setLoading(true);
        try {
            const [projectsRes, customersRes, developersRes] = await Promise.all([
                api.get('/projects'),
                api.get('/customers?limit=100'),
                api.get('/developers?limit=100')
            ]);
            setProjects(projectsRes.data);
            setCustomers(customersRes.data.customers);
            setDevelopers(developersRes.data.developers);
        } catch (err) {
            console.error('Error fetching data', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const filteredProjects = useMemo(() => {
        return (projects as any[]).filter((p: any) => {
            if (searchName && !p.projectName?.toLowerCase().includes(searchName.toLowerCase())) return false;
            if (filterCustomer && p.customerId !== filterCustomer) return false;
            if (filterDateFrom) {
                const date = p.startDate || p.createdAt;
                if (date && new Date(date) < new Date(filterDateFrom)) return false;
            }
            if (filterDateTo) {
                const date = p.startDate || p.createdAt;
                if (date && new Date(date) > new Date(filterDateTo)) return false;
            }
            return true;
        });
    }, [projects, searchName, filterCustomer, filterDateFrom, filterDateTo]);

    const handleCreate = () => { setEditingProject(null); setShowForm(true); };
    const handleEdit = (project: any) => { setEditingProject(project); setShowForm(true); };
    const handleDelete = async (project: any) => {
        if (window.confirm('Are you sure you want to delete this project?')) {
            try {
                await api.delete(`/projects/${project.id}`);
                fetchData();
            } catch (err) {
                console.error('Error deleting project', err);
            }
        }
    };

    const columns = [
        { header: 'Customer Name', accessor: (item: any) => item.customer?.displayName || 'Unknown' },
        { header: 'Project Name', accessor: 'projectName' },
        { header: 'Billing Method', accessor: 'billingMethod' },
        { header: 'Rate', accessor: (item: any) => formatCurrency(item.hourlyRate, currency) },
        {
            header: 'Remaining Amount',
            accessor: (item: any) => {
                if (item.billingMethod === 'Fixed Cost') {
                    const remaining = (item.hourlyRate || 0) - (item.receivedAmount || 0);
                    return formatCurrency(remaining, currency);
                }
                return '-';
            }
        },
        {
            header: 'Total Amount',
            accessor: (item: any) => {
                const rate = item.hourlyRate || 0;
                let subtotal = 0;
                if (item.billingMethod === 'Fixed Cost') {
                    subtotal = rate;
                } else if (rate && item.timesheets && item.timesheets.length > 0) {
                    const totalHours = item.timesheets.reduce((acc: number, t: any) => {
                        const [h, m] = t.timeSpent.split(':').map(Number);
                        return acc + (h + m / 60);
                    }, 0);
                    subtotal = totalHours * rate;
                }
                return formatCurrency(subtotal, currency);
            }
        },
        { header: 'Tasks', accessor: (item: any) => item._count?.tasks || 0 }
    ];

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold text-gray-800">Projects</h1>
                <button
                    onClick={handleCreate}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
                >
                    <Plus size={18} /> New
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4 bg-white p-3 rounded-lg border border-gray-200">
                <div className="relative flex-1 min-w-[160px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search project name..."
                        value={searchName}
                        onChange={e => setSearchName(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </div>
                <select
                    value={filterCustomer}
                    onChange={e => setFilterCustomer(e.target.value)}
                    className="flex-1 min-w-[160px] px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                    <option value="">All Clients</option>
                    {(customers as any[]).map((c: any) => (
                        <option key={c.id} value={c.id}>{c.displayName}</option>
                    ))}
                </select>
                <div className="flex items-center gap-2">
                    <input
                        type="date"
                        value={filterDateFrom}
                        onChange={e => setFilterDateFrom(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <span className="text-gray-400 text-sm">—</span>
                    <input
                        type="date"
                        value={filterDateTo}
                        onChange={e => setFilterDateTo(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </div>
                {(searchName || filterCustomer || filterDateFrom || filterDateTo) && (
                    <button
                        onClick={() => { setSearchName(''); setFilterCustomer(''); setFilterDateFrom(''); setFilterDateTo(''); }}
                        className="px-3 py-1.5 text-sm text-gray-500 hover:text-red-500 border border-gray-300 rounded-md"
                    >
                        Clear
                    </button>
                )}
            </div>

            <DataTable
                data={filteredProjects}
                columns={columns}
                total={filteredProjects.length}
                page={1}
                limit={100}
                onPageChange={() => { }}
                onEdit={handleEdit}
                onDelete={handleDelete}
                loading={loading}
            />

            {showForm && (
                <ProjectForm
                    onClose={() => setShowForm(false)}
                    onSuccess={() => { setShowForm(false); fetchData(); }}
                    project={editingProject}
                    customers={customers}
                    developers={developers}
                />
            )}
        </div>
    );
};

export default Projects;
